/* ──────────────────────────────────────────────────────────────────────────
   What the desktop quick bar finds for what was typed (Jay, 2026-10-01).

   Pure, so it is tested without a browser (apps/web/tests/quick-search.test.ts).

   Every match is listed in one ranked list: tabs, pages and creators side by
   side. A creator's name followed by a space lists that creator's pages
   ("lexi " offers Profile, Reporting, Links, Uploads) and the words after the
   space narrow them, the way Raycast suggests a command's arguments.
   ────────────────────────────────────────────────────────────────────────── */

export type QuickPlace = { id: string; label: string; to: string; kind: "tab" | "page"; hint?: string; keywords?: string };
export type QuickCreator = { name: string; photo: string | null; departed: boolean };
export type QuickCreatorPage = { id: string; label: string; keywords?: string };

export type QuickResult =
  | { id: string; kind: "place"; score: number; place: QuickPlace }
  | { id: string; kind: "creator"; score: number; creator: QuickCreator }
  | { id: string; kind: "creator-page"; score: number; creator: QuickCreator; page: QuickCreatorPage };

/** A row under the stats when nothing is typed: somewhere the person went lately. */
export type QuickRecent = { to: string; label: string; kind: "tab" | "page" | "creator" | "creator-page"; creator?: string };

const words = (text: string) => text.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);

/** Each letter of `needle` appears in `hay`, in order. */
function inOrder(needle: string, hay: string): boolean {
  let at = 0;
  for (const ch of needle) {
    at = hay.indexOf(ch, at);
    if (at === -1) return false;
    at += 1;
  }
  return true;
}

/**
 * How well `query` matches a label, 0 for not at all. Exact beats a prefix,
 * which beats the start of a later word, then anywhere inside, then the
 * extra keywords, then letters in order ("lxi" finds LEXI).
 */
export function scoreText(query: string, label: string, keywords = ""): number {
  const q = query.trim().toLowerCase().replace(/\s+/g, " ");
  if (!q) return 0;
  const l = label.toLowerCase();
  if (l === q) return 100;
  if (l.startsWith(q)) return 90;
  const labelWords = words(label);
  if (labelWords.some((w) => w.startsWith(q))) return 75;
  if (l.includes(q)) return 60;
  const tokens = words(q);
  const extra = [...labelWords, ...words(keywords)];
  if (tokens.length > 1 && tokens.every((t) => extra.some((w) => w.startsWith(t)))) return 50;
  if (words(keywords).some((w) => w.startsWith(q))) return 40;
  if (q.length >= 2 && !q.includes(" ") && inOrder(q, l.replace(/\s+/g, ""))) return 20;
  return 0;
}

/**
 * The creators a query is about and what follows them: "lexi links" is LEXI
 * and "links". A full name (spaces and all) wins; otherwise the first word as
 * the start of a name ("lex links"). Only once a space has been typed, so
 * "lexi" alone is the creator and not her pages.
 */
function creatorScopes(raw: string, creators: QuickCreator[]): { creator: QuickCreator; rest: string }[] {
  const typed = raw.replace(/^\s+/, "").replace(/\s+/g, " ").toLowerCase();
  if (!typed.includes(" ")) return [];
  const full = creators
    .filter((c) => typed.startsWith(`${c.name.toLowerCase()} `))
    .sort((a, b) => b.name.length - a.name.length);
  if (full.length) return [{ creator: full[0]!, rest: typed.slice(full[0]!.name.length + 1).trim() }];
  const head = typed.slice(0, typed.indexOf(" "));
  const rest = typed.slice(typed.indexOf(" ") + 1).trim();
  return creators.filter((c) => c.name.toLowerCase().startsWith(head)).map((creator) => ({ creator, rest }));
}

export function searchQuick(
  query: string,
  { places, creators, creatorPages }: { places: QuickPlace[]; creators: QuickCreator[]; creatorPages: QuickCreatorPage[] },
): QuickResult[] {
  if (!query.trim()) return [];
  const results: QuickResult[] = [];

  // A creator's pages come first when the query is about her: she was named
  // on purpose. Their own order is kept when nothing narrows them.
  for (const { creator, rest } of creatorScopes(query, creators)) {
    creatorPages.forEach((page, index) => {
      const fit = rest ? scoreText(rest, page.label, page.keywords) : 100 - index;
      if (fit > 0) results.push({ id: `creator-page:${creator.name}:${page.id}`, kind: "creator-page", score: 1000 + fit - (creator.departed ? 5 : 0), creator, page });
    });
  }

  const q = query.trim();
  for (const place of places) {
    const score = scoreText(q, place.label, `${place.keywords ?? ""} ${place.hint ?? ""}`);
    // A tab outranks a page that scores the same: "links" is the tab, not "Links settings".
    if (score > 0) results.push({ id: `place:${place.id}`, kind: "place", score: score + (place.kind === "tab" ? 2 : 0), place });
  }
  for (const creator of creators) {
    const score = scoreText(q, creator.name);
    if (score > 0) results.push({ id: `creator:${creator.name}`, kind: "creator", score: score + 1 - (creator.departed ? 5 : 0), creator });
  }

  const label = (r: QuickResult) => (r.kind === "place" ? r.place.label : r.creator.name);
  return results.sort((a, b) => b.score - a.score || label(a).localeCompare(label(b)));
}

// ── the clipboard ─────────────────────────────────────────────────────────

export type ClipboardHint = { kind: "instagram"; handle: string } | { kind: "url"; host: string; path: string };

// The first part of an Instagram address that is a page of Instagram's own
// rather than an account.
const INSTAGRAM_PAGES = new Set(["p", "reel", "reels", "tv", "explore", "accounts", "direct", "about", "legal", "developer"]);
const HANDLE = /^[A-Za-z0-9._]{1,30}$/;

/**
 * What the copied text could be opened as: an Instagram account (a handle or
 * a profile address) or a web address that may be one of our links. Anything
 * else is null and the bar offers nothing for it.
 */
export function parseClipboard(text: string): ClipboardHint | null {
  const value = text.trim();
  if (!value || value.length > 500 || /\s/.test(value)) return null;
  if (value.startsWith("@")) return HANDLE.test(value.slice(1)) ? { kind: "instagram", handle: value.slice(1) } : null;
  let url: URL;
  try {
    url = new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(value) ? value : `https://${value}`);
  } catch {
    return null;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return null;
  const host = url.hostname.toLowerCase();
  if (!host.includes(".")) return null;
  const parts = url.pathname.split("/").filter(Boolean);
  if (host === "instagram.com" || host.endsWith(".instagram.com")) {
    const first = parts[0]?.toLowerCase();
    if (first === "stories") return parts[1] && HANDLE.test(parts[1]) ? { kind: "instagram", handle: parts[1] } : null;
    if (!first || INSTAGRAM_PAGES.has(first) || !HANDLE.test(parts[0]!)) return null;
    return { kind: "instagram", handle: parts[0]! };
  }
  return { kind: "url", host, path: parts.join("/").toLowerCase() };
}

type LinkIndex = {
  domains: { id: string; hostname: string }[];
  links: { id: string; domainId: string; slug: string; deletedAt?: string | null }[];
  aliases?: { domainId: string; slug: string; linkId: string }[];
};

/** The link a copied address points at, by its domain and slug, or by an old slug it answered to. */
export function findLinkByUrl(at: { host: string; path: string }, { domains, links, aliases = [] }: LinkIndex): string | null {
  const bare = at.host.replace(/^www\./, "");
  const domain = domains.find((d) => d.hostname.toLowerCase() === at.host) ?? domains.find((d) => d.hostname.toLowerCase().replace(/^www\./, "") === bare);
  if (!domain) return null;
  const slug = at.path.replace(/^\/+|\/+$/g, "").toLowerCase();
  const live = links.filter((l) => l.domainId === domain.id && !l.deletedAt);
  const direct = live.find((l) => l.slug.replace(/^\/+|\/+$/g, "").toLowerCase() === slug);
  if (direct) return direct.id;
  const alias = aliases.find((a) => a.domainId === domain.id && a.slug.toLowerCase() === slug);
  return alias && live.some((l) => l.id === alias.linkId) ? alias.linkId : null;
}

// ── recents ───────────────────────────────────────────────────────────────

// Creators pages that are not a creator: /creators/notes is the notes page.
const CREATOR_STATIC = new Set(["notes", "settings", "onboarding", "links", "sign-ins"]);

/** A page the main window showed, as a recents row, or null for a page with no name worth showing. */
export function describePath(path: string, places: QuickPlace[]): QuickRecent | null {
  const pathname = path.split(/[?#]/)[0]!;
  const place = places.find((p) => p.to === pathname);
  if (place) return { to: pathname, label: place.label, kind: place.kind };
  const creator = /^\/creators\/([^/]+)$/.exec(pathname)?.[1];
  if (creator) {
    let name: string;
    try {
      name = decodeURIComponent(creator);
    } catch {
      return null;
    }
    if (CREATOR_STATIC.has(name.toLowerCase())) return null;
    return { to: pathname, label: name, kind: "creator", creator: name };
  }
  return null;
}

export const RECENTS_KEPT = 8;

export function pushRecent<T extends { to: string }>(list: T[], item: T, keep = RECENTS_KEPT): T[] {
  return [item, ...list.filter((r) => r.to !== item.to)].slice(0, keep);
}
